import type { BudgetInfoComparison } from "../types/budget/comparison-types";

/** Igual ao `isDomesticCurrency` do export comparativo (BRL/REAL). */
export function isDomesticComparisonCurrency(currency: unknown): boolean {
  const cur =
    String(currency ?? "BRL")
      .trim()
      .toUpperCase() || "BRL";
  return cur === "BRL" || cur === "REAL";
}

function serviceIncludedInBudgetVersion(
  service: any,
  version: number
): boolean {
  const vList = service?.versions;
  if (!Array.isArray(vList) || vList.length === 0) {
    return true;
  }
  return vList.some((v: any) => Number(v) === Number(version));
}

function serviceExchangeMatchesTaxType(sv: any, taxType: string): boolean {
  const domestic = isDomesticComparisonCurrency(sv?.exchangeData?.currency);
  if (taxType === "iss") return domestic;
  if (taxType === "iof") return !domestic;
  return true;
}

function baseValueExchangeMatchesTaxType(
  baseVal: any,
  taxType: string
): boolean {
  const cur =
    String(baseVal?.exchange?.currency ?? "BRL")
      .trim()
      .toUpperCase() || "BRL";
  const domestic = cur === "BRL" || cur === "REAL";
  if (taxType === "iss") return domestic;
  if (taxType === "iof") return !domestic;
  return true;
}

/**
 * Presença de moeda nos serviços do comparativo (mesma ideia de
 * `inspectSectionsCurrencyPresence` na API de export).
 */
export function inspectComparisonBudgetsCurrencyPresence(
  budgets: BudgetInfoComparison[]
): { hasBrl: boolean; hasForeign: boolean; sawService: boolean } {
  const out = { hasBrl: false, hasForeign: false, sawService: false };
  for (const bi of budgets || []) {
    const st = bi.status || "OPEN";
    const ver = bi.version ?? 0;
    for (const section of bi.sections || []) {
      const sec = section as any;
      const flat: any[] = [];
      if (Array.isArray(section?.allServices) && section.allServices.length) {
        flat.push(...section.allServices);
      } else {
        const sp = sec?.servicesPerSupplier;
        if (sp && typeof sp === "object") {
          for (const supplierId of Object.keys(sp)) {
            const categories = sp[supplierId];
            if (!categories || typeof categories !== "object") continue;
            for (const category of Object.keys(categories)) {
              const list = categories[category];
              if (Array.isArray(list)) flat.push(...list);
            }
          }
        }
      }
      for (const service of flat) {
        if (!serviceIncludedInBudgetVersion(service, ver)) continue;
        const sv = resolveComparisonServiceValues(service, st, ver);
        if (!sv) continue;
        out.sawService = true;
        if (isDomesticComparisonCurrency(sv.exchangeData?.currency)) {
          out.hasBrl = true;
        } else {
          out.hasForeign = true;
        }
      }
    }
  }
  return out;
}

/**
 * Igual `getBudgetComparisonsCurrencyTaxFlags` na API (export comparativo): só moeda
 * dos serviços — sem escopo do evento, para bater com a planilha.
 */
export function getComparisonSummaryTaxFlags(budgets: BudgetInfoComparison[]): {
  showIssSummary: boolean;
  showIofSummary: boolean;
} {
  const p = inspectComparisonBudgetsCurrencyPresence(budgets);
  return {
    showIssSummary: p.sawService ? p.hasBrl : true,
    showIofSummary: p.sawService ? p.hasForeign : false,
  };
}

export type ComparisonCategoryTotalRowDef = { key: string; label: string };

/**
 * Mesma ordem e rótulos de `COMPARATIVE_CATEGORY_SUMMARY_LABELS` +
 * `activeCategorySummaryLabels` em `export-comparative-sheet.service.ts`:
 * Taxa de Serviço > ISS/IOF > CBS > IBS > Imposto > Taxa MM (FEE) (#MMV-2539).
 * CBS/IBS sempre aparecem (igual ao Excel — não têm um gate de moeda como
 * ISS/IOF); a API soma esses totais por categoria desde #MMV-2539.
 * Chaves = chaves lógicas do Excel (`costOfCategory`, `taxesTotalCost`, …).
 */
export function buildComparisonCategoryTotalRows(flags: {
  showIssSummary: boolean;
  showIofSummary: boolean;
}): ComparisonCategoryTotalRowDef[] {
  const rows: ComparisonCategoryTotalRowDef[] = [
    { key: "costOfCategory", label: "Custos da Categoria" },
    { key: "serviceTax", label: "Taxa de serviço" },
  ];
  if (flags.showIssSummary) rows.push({ key: "iss", label: "ISS" });
  if (flags.showIofSummary) rows.push({ key: "iof", label: "IOF" });
  rows.push(
    { key: "cbs", label: "CBS" },
    { key: "ibs", label: "IBS" },
    { key: "tribute", label: "Imposto MM" },
    { key: "taxesTotalCost", label: "Custos totais de taxas + impostos" },
    { key: "subtotalForecast", label: "Subtotal Previsão de Custos" },
    { key: "fee", label: "Taxa MM Eventos" },
    {
      key: "totalAccommodationTaxesFeeMM",
      label: "Custos Totais + Taxas + FEE MM",
    }
  );
  return rows;
}

function nCategoryTotalBrl(x: any): number {
  const v = x?.totals?.totalInBRL;
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

/** Soma da faixa `taxesTotalCost` no Excel (`getTotalValueForSummaryItem` case taxesTotalCost). */
export function getExportParityTaxesTotalCostBrl(
  ct: any,
  flags: { showIssSummary: boolean; showIofSummary: boolean }
): number {
  if (!ct) return 0;
  let s =
    nCategoryTotalBrl(ct.serviceTax) +
    nCategoryTotalBrl(ct.tribute) +
    nCategoryTotalBrl(ct.cbs) +
    nCategoryTotalBrl(ct.ibs) +
    nCategoryTotalBrl(ct.localTax) +
    nCategoryTotalBrl(ct.tourismTax) +
    nCategoryTotalBrl(ct.doubleCharged);
  if (flags.showIssSummary) s += nCategoryTotalBrl(ct.iss);
  if (flags.showIofSummary) s += nCategoryTotalBrl(ct.iof);
  return s;
}

/**
 * Coluna "Valor Unitário" nas linhas de resumo: vazia nos blocos compostos;
 * % nas linhas de taxa — espelha `fillComparativeSummaryValorTotalAndTaxVu` no export.
 */
export function getExportParityCategorySummaryVu(
  summaryKey: string,
  b: BudgetInfoComparison,
  sectionKey: string,
  categoryKey: string
): string {
  if (
    summaryKey === "costOfCategory" ||
    summaryKey === "taxesTotalCost" ||
    summaryKey === "subtotalForecast" ||
    summaryKey === "totalAccommodationTaxesFeeMM"
  ) {
    return "";
  }
  const taxRollup = new Set([
    "serviceTax",
    "tribute",
    "iss",
    "iof",
    "cbs",
    "ibs",
    "fee",
  ]);
  if (taxRollup.has(summaryKey)) {
    return getComparisonCategoryTaxPercentageDisplay(
      b,
      sectionKey,
      categoryKey,
      summaryKey
    );
  }
  return "";
}

/** Coluna "Valor Total" (BRL) por linha de resumo — espelha `getTotalValueForSummaryItem` na API. */
export function getExportParityCategorySummaryTotalBrl(
  summaryKey: string,
  ct: any,
  flags: { showIssSummary: boolean; showIofSummary: boolean }
): number {
  if (!ct) return 0;
  const taxes = () => getExportParityTaxesTotalCostBrl(ct, flags);
  switch (summaryKey) {
    case "costOfCategory":
      return nCategoryTotalBrl(ct.categoryTariffs);
    case "serviceTax":
      return nCategoryTotalBrl(ct.serviceTax);
    case "tribute":
      return nCategoryTotalBrl(ct.tribute);
    case "iss":
      return nCategoryTotalBrl(ct.iss);
    case "iof":
      return nCategoryTotalBrl(ct.iof);
    case "cbs":
      return nCategoryTotalBrl(ct.cbs);
    case "ibs":
      return nCategoryTotalBrl(ct.ibs);
    case "taxesTotalCost":
      return taxes();
    case "subtotalForecast":
      return nCategoryTotalBrl(ct.categoryTariffs) + taxes();
    case "fee":
      return nCategoryTotalBrl(ct.fee);
    case "totalAccommodationTaxesFeeMM":
      return nCategoryTotalBrl(ct.servicesSumTotal);
    default:
      return 0;
  }
}

function isNumericTariffValue(v: unknown): boolean {
  return typeof v === "number" && Number.isFinite(v);
}

/**
 * Total do tarifário na moeda exibida no item (USD etc.), mesma regra de
 * `calculateTariff` em `Api/src/modules/budget/utils/utils.ts` (DAILY / SERVICE / else).
 * Retorna null quando o valor não representa montante em moeda (ex.: %).
 */
function foreignTariffLineTotalInDisplayCurrency(
  sv: any,
  taxData: any
): number | null {
  const tariff = taxData?.tariff;
  if (!tariff) return null;
  const tt = String(tariff.typeTax ?? "");
  if (
    tt === "CORTESY" ||
    tt === "INCLUDED" ||
    tt === "AC_CLIENT" ||
    tt === "PERCENTAGE" ||
    tt === "%" ||
    tt === "PERCENTAGE_RJ" ||
    tt === "%RJ"
  ) {
    return null;
  }
  const v = Number(tariff.value);
  if (!Number.isFinite(v)) return null;
  const qty = Number(sv.quantity ?? 0);
  const daily = Number(sv.daily ?? 0);
  if (tt === "DAILY") {
    if (!Number.isFinite(qty) || !Number.isFinite(daily)) return null;
    return v * qty * daily;
  }
  if (tt === "SERVICE") {
    if (!Number.isFinite(qty)) return null;
    return v * qty;
  }
  return v;
}

/**
 * Total do tarifário em BRL (linha inteira, antes de ÷ qtde×diárias).
 * Moeda estrangeira: prioriza `valor na tela × câmbio do serviço` (coerente com o
 * orçamento em US$); depois `totalServiceConverted`, `fullCoinService × câmbio`,
 * `valueBRL`. BRL: usa `valueBRL`.
 */
function tariffLineTotalBrlForComparisonClient(sv: any, taxData: any): number {
  const t = taxData?.tariff;
  const vb = Number(t?.valueBRL ?? 0);
  if (isDomesticComparisonCurrency(sv?.exchangeData?.currency)) {
    return Number.isFinite(vb) ? vb : 0;
  }
  const rate = Number(sv?.exchangeData?.value ?? 0);
  const foreignLine = foreignTariffLineTotalInDisplayCurrency(sv, taxData);
  if (
    foreignLine !== null &&
    Number.isFinite(foreignLine) &&
    Number.isFinite(rate) &&
    Math.abs(rate) > 1e-9
  ) {
    const brl = foreignLine * rate;
    if (Number.isFinite(brl)) return brl;
  }

  const conv = Number(t?.totalServiceConverted ?? 0);
  if (Number.isFinite(conv) && Math.abs(conv) > 1e-9) return conv;
  const full = Number(t?.fullCoinService ?? 0);
  if (
    Number.isFinite(full) &&
    Number.isFinite(rate) &&
    Math.abs(full) > 1e-9 &&
    Math.abs(rate) > 1e-9
  ) {
    return full * rate;
  }
  return Number.isFinite(vb) ? vb : 0;
}

/**
 * Valor unitário exibido no comparativo (CLIENT): BRL com markup por unidade
 * quando aplicável, espelhando `computeClientMarkupPerUnitAndTariffUnitForExport` /
 * `defineValorUnAndValorTotal` do export comparativo.
 */
export function getComparisonServiceValorUnitarioDisplay(
  sv: any,
  isClientExport: boolean
): string | number {
  if (!sv?.taxData?.tariff) {
    return 0;
  }
  const tariff = sv.taxData.tariff;
  if (tariff.typeTax === "CORTESY") return "Cortesia";
  if (tariff.typeTax === "INCLUDED") return "Incluso";
  if (tariff.typeTax === "AC_CLIENT") return "A/C Cliente";

  const displayValorUn = tariff.value;
  const isNumericTariffForRow = isNumericTariffValue(displayValorUn);

  const taxData = sv.taxData || {};
  const markupBrl = Number(taxData.markup?.valueBRL ?? 0);
  const unitsRaw = Number(sv.quantity ?? 0) * Number(sv.daily ?? 0);
  const units = Number.isFinite(unitsRaw) ? unitsRaw : 0;
  const markupPerUnitBrl = units > 0 ? markupBrl / units : 0;
  const denom = units > 0 ? units : 1;
  const valueBrlPart =
    tariffLineTotalBrlForComparisonClient(sv, taxData) / denom;

  const tariffUnitBrl =
    isClientExport && isNumericTariffForRow
      ? valueBrlPart + Number(markupPerUnitBrl || 0)
      : valueBrlPart;

  if (!isNumericTariffForRow) {
    if (typeof displayValorUn === "string") return displayValorUn;
    return tariffUnitBrl;
  }
  return tariffUnitBrl;
}

/**
 * Valor total da linha no comparativo (CLIENT): espelha `defineValorUnAndValorTotal`
 * em `export-comparative-sheet.service.ts` — coluna visível = Qtde × Diárias × VU
 * (VU já inclui markup por unidade quando `isClientExport`), não `totals.totalInBRL`.
 */
export function getComparisonServiceValorTotalDisplay(
  sv: any,
  isClientExport: boolean
): string | number {
  if (!sv?.taxData?.tariff) {
    const n = Number(sv?.totals?.totalInBRL ?? 0);
    return Number.isFinite(n) ? n : 0;
  }
  const tariff = sv.taxData.tariff;
  if (tariff.typeTax === "CORTESY") return "Cortesia";
  if (tariff.typeTax === "INCLUDED") return "Incluso";
  if (tariff.typeTax === "AC_CLIENT") return "A/C Cliente";

  const qty = Number(sv.quantity ?? 0);
  const daily = Number(sv.daily ?? 0);
  const qd = Number.isFinite(qty) && Number.isFinite(daily) ? qty * daily : 0;

  const vuCell = getComparisonServiceValorUnitarioDisplay(sv, isClientExport);
  const displayValorUn = tariff.value;
  const isNumericTariffForRow = isNumericTariffValue(displayValorUn);

  if (isNumericTariffForRow) {
    if (typeof vuCell === "number" && Number.isFinite(vuCell)) {
      return qd * vuCell;
    }
  }
  if (typeof vuCell === "number" && Number.isFinite(vuCell)) {
    return qd * vuCell;
  }
  return typeof vuCell === "string" ? vuCell : 0;
}

/** Igual ao `getServiceValuesByStatus` da API — valores por status/versão. */
export function resolveComparisonServiceValues(
  svc: any,
  status: string,
  version: number
): any {
  const s = (status || "OPEN").toUpperCase();
  if (version === 0 || s === "OPEN") return svc.budgetValues;
  if (s === "APPROVED" || s === "APROVED") return svc.closingValues;
  if (s === "NEGOTIATE") {
    return (
      svc.negotiationValue?.[version - 1] ??
      svc.negotiationValue?.[svc.negotiationValue?.length - 1]
    );
  }
  return svc.budgetValues;
}

export function isComparisonTaxPercentageType(
  tax: any,
  taxKey: string
): boolean {
  if (!tax?.typeTax) return false;
  const t = tax.typeTax as string;
  if (t === "PERCENTAGE" || t === "%") return true;
  if (taxKey === "iss" && (t === "PERCENTAGE_RJ" || t === "%RJ")) return true;
  return false;
}

function formatTaxPercentValue(tax: { value?: number }): string {
  const pct = tax.value ?? 0;
  return Number.isInteger(pct) ? `${pct}%` : `${Number(pct).toFixed(2)}%`;
}

type TaxesPerSupplier = Record<string, Record<string, any[]>>;

function pickTaxFromBaseValues(
  taxesPerSupplier: TaxesPerSupplier | undefined,
  categoryKey: string,
  taxType: string,
  requirePositiveValue: boolean
): string | null {
  if (!taxesPerSupplier || typeof taxesPerSupplier !== "object") return null;

  const tryEntry = (baseVal: any): string | null => {
    if (
      (taxType === "iss" || taxType === "iof") &&
      !baseValueExchangeMatchesTaxType(baseVal, taxType)
    ) {
      return null;
    }
    const tax = baseVal?.taxData?.[taxType];
    if (!tax || !isComparisonTaxPercentageType(tax, taxType)) return null;
    const v = tax.value ?? 0;
    if (requirePositiveValue && v <= 0) return null;
    return formatTaxPercentValue(tax);
  };

  for (const supId of Object.keys(taxesPerSupplier)) {
    const arr = taxesPerSupplier[supId]?.[categoryKey];
    if (!Array.isArray(arr)) continue;
    for (const baseVal of arr) {
      const hit = tryEntry(baseVal);
      if (hit) return hit;
    }
  }
  return null;
}

/**
 * Percentual exibido nas linhas de taxa do comparativo.
 * Cortesia / incluso / A.C. cliente zeram valores nos `budgetValues`, mas as taxas
 * configuradas seguem em `taxesPerSupplier` (baseValues por fornecedor e categoria).
 */
export function getComparisonCategoryTaxPercentageDisplay(
  b: BudgetInfoComparison,
  sectionKey: string,
  categoryKey: string,
  taxType: string
): string {
  const section = b.sections?.find((s) => s.key === sectionKey);
  if (!section) return "0%";

  const categoryServices =
    section.allServices?.filter((sv: any) => sv.type === categoryKey) ?? [];

  const pickFromService = (requirePositive: boolean): string | null => {
    for (const svc of categoryServices) {
      const sv = resolveComparisonServiceValues(svc, b.status, b.version);
      if (
        (taxType === "iss" || taxType === "iof") &&
        !serviceExchangeMatchesTaxType(sv, taxType)
      ) {
        continue;
      }
      const tax = sv?.taxData?.[taxType];
      if (!tax || !isComparisonTaxPercentageType(tax, taxType)) continue;
      const v = tax.value ?? 0;
      if (requirePositive && v <= 0) continue;
      return formatTaxPercentValue(tax);
    }
    return null;
  };

  const fromSvc = pickFromService(true);
  if (fromSvc) return fromSvc;

  const taxesPerSupplier = section.taxesPerSupplier as
    | TaxesPerSupplier
    | undefined;
  const fromBasePositive = pickTaxFromBaseValues(
    taxesPerSupplier,
    categoryKey,
    taxType,
    true
  );
  if (fromBasePositive) return fromBasePositive;

  const fromBaseAny = pickTaxFromBaseValues(
    taxesPerSupplier,
    categoryKey,
    taxType,
    false
  );
  if (fromBaseAny) return fromBaseAny;

  const fromSvcAny = pickFromService(false);
  if (fromSvcAny) return fromSvcAny;

  const wide = b.budgetWideTaxData;
  if (wide) {
    const tax = (wide as Record<string, any>)[taxType];
    if (tax && isComparisonTaxPercentageType(tax, taxType)) {
      return formatTaxPercentValue(tax);
    }
  }

  return "0%";
}
